import { useState, useEffect, useRef } from 'react';
import { getNameDayNames } from '@/lib/polishNameDays';
import { scheduleNonCriticalTask } from '@/lib/nonCriticalTask';
import { isPrerenderMode } from '@/lib/isPrerenderMode';
import { useDeliveryCountdown, warsawDetailed, addDaysISO, type BannerState } from '@/lib/useDeliveryCountdown';
import type { TodayDeliveryBannerConfig } from '@/lib/sections/types';

/* ─── Slot definitions (mirrors CheckoutDeliveryForm) ─────────── */
const WEEKDAY_BANNER_SLOTS = [
  { label: '9:00–11:00',  cutoffHour: 8  },
  { label: '11:00–13:00', cutoffHour: 10 },
  { label: '13:00–15:00', cutoffHour: 12 },
  { label: '15:00–17:00', cutoffHour: 14 },
  { label: '17:00–19:00', cutoffHour: 16 },
  { label: '19:00–21:00', cutoffHour: 18 },
];

const SUNDAY_BANNER_SLOTS = [
  { label: '10:00–13:00', cutoffHour: 9  },
  { label: '13:00–16:00', cutoffHour: 12 },
];

/* ─── Polish name popularity ranking (GUS data, most popular first) ── */
const NAME_POPULARITY: Record<string, number> = (() => {
  const ordered = [
    // Women
    'Anna','Maria','Katarzyna','Małgorzata','Agnieszka','Barbara','Ewa','Zofia','Elżbieta',
    'Joanna','Magdalena','Monika','Teresa','Krystyna','Karolina','Aleksandra','Natalia',
    'Marta','Justyna','Beata','Weronika','Paulina','Dorota','Bożena','Danuta','Irena',
    'Halina','Grażyna','Iwona','Sylwia','Renata','Patrycja','Edyta','Kamila','Alicja',
    'Klaudia','Milena','Kinga','Ewelina','Olga','Zuzanna','Julia','Amelia',
    // Men
    'Jan','Andrzej','Piotr','Krzysztof','Stanisław','Tomasz','Paweł','Michał','Marcin',
    'Marek','Łukasz','Grzegorz','Adam','Jerzy','Tadeusz','Dariusz','Zbigniew','Ryszard',
    'Henryk','Aleksander','Józef','Wojciech','Rafał','Artur','Bartosz','Maciej','Karol',
    'Robert','Mariusz','Jacek','Szymon','Kamil','Mateusz','Jakub','Filip','Dominik',
    'Sebastian','Damian','Radosław','Hubert','Konrad','Przemysław','Mirosław','Leszek',
  ];
  const map: Record<string, number> = {};
  ordered.forEach((n, i) => { map[n] = i; });
  return map;
})();

function sortByPopularity(names: string[]): string[] {
  return [...names].sort((a, b) => {
    const ra = NAME_POPULARITY[a] ?? 9999;
    const rb = NAME_POPULARITY[b] ?? 9999;
    return ra - rb;
  });
}


const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, '');

/* ─── Fetch name days through our server-side cache ─────────────── */
async function fetchNameDays(month: number, day: number): Promise<string[]> {
  const cacheKey = `bukietowa_namedays_${new Date().getFullYear()}_${month}_${day}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached) as string[];
  } catch { /* ignore */ }

  try {
    const res = await fetch(`${BASE_URL}/api/name-days?month=${month}&day=${day}`, {
      signal: AbortSignal.timeout(6000),
    });
    if (!res.ok) return [];
    const data = await res.json() as { names?: unknown };
    const names = Array.isArray(data.names)
      ? data.names.filter((name): name is string => typeof name === 'string')
      : [];

    if (names.length > 0) {
      try { sessionStorage.setItem(cacheKey, JSON.stringify(names)); } catch { /* ignore */ }
    }
    return names;
  } catch {
    return [];
  }
}

function getNamedayDate(): { month: number; day: number; isTomorrow: boolean } {
  const { dateISO, hour, month, day } = warsawDetailed();
  if (hour >= 17) {
    const tomorrowISO = addDaysISO(dateISO, 1);
    const [, tm, td] = tomorrowISO.split('-').map(Number);
    return { month: tm, day: td, isTomorrow: true };
  }
  return { month, day, isTomorrow: false };
}

/* ─── Design tokens ─────────────────────────────────────────── */
const DARK    = '#3a2d22';
const GOLD    = '#e05590';
const GOLD2   = '#806126';
const ROSE    = '#b83f60';
const GRAY    = '#6b6560';

/* ─── Sub-components ─────────────────────────────────────────── */

/** Number block + abbreviated unit label, side by side */
function TimeBlock({ value, unit }: { value: string; unit: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
      <span style={{
        background: GOLD,
        color: '#1a1410',
        fontFamily: 'DM Mono, DM Sans, monospace',
        fontSize: 'clamp(14px, 1.6vw, 18px)',
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: '0.01em',
        padding: '5px 0',
        borderRadius: 4,
        width: 40,
        textAlign: 'center',
        fontVariantNumeric: 'tabular-nums',
        boxShadow: '0 1px 3px rgba(210,60,120,0.28)',
        display: 'inline-block',
        flexShrink: 0,
      }}>
        {value}
      </span>
      <span style={{
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 'clamp(9px, 0.85vw, 11px)',
        letterSpacing: '0.08em',
        color: GRAY,
        textTransform: 'uppercase' as const,
        fontWeight: 500,
        width: 32,          /* fixed so "GODZ." / "MIN." / "SEK." don't differ */
        display: 'inline-block',
        flexShrink: 0,
      }}>
        {unit}
      </span>
    </span>
  );
}

/** Outline clock icon — gold, matching reference image style */
function ClockIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true"
      style={{ flexShrink: 0 }}>
      <circle cx="13" cy="13" r="11" stroke={GOLD} strokeWidth="1.6" />
      <path d="M13 8v5.5l3.5 2" stroke={GOLD} strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Gift/present icon — rose colour, matching reference image */
function GiftIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true"
      style={{ flexShrink: 0 }}>
      {/* Box body */}
      <rect x="3" y="12" width="20" height="11" rx="1.5" stroke={ROSE} strokeWidth="1.5" />
      {/* Lid */}
      <rect x="2" y="9" width="22" height="3.5" rx="1" stroke={ROSE} strokeWidth="1.5" />
      {/* Ribbon vertical */}
      <line x1="13" y1="9" x2="13" y2="23" stroke={ROSE} strokeWidth="1.5" />
      {/* Ribbon bow */}
      <path d="M13 9 C11 7 8 7 8 9.5 C8 11.5 13 9 13 9Z" stroke={ROSE} strokeWidth="1.2" fill="none" />
      <path d="M13 9 C15 7 18 7 18 9.5 C18 11.5 13 9 13 9Z" stroke={ROSE} strokeWidth="1.2" fill="none" />
    </svg>
  );
}

/* ─── Scrolling name-days ticker ───────────────────────────────── */
const TICKER_STYLE_ID = 'bukietowa-ticker-anim';

function ensureTickerStyles() {
  if (document.getElementById(TICKER_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = TICKER_STYLE_ID;
  style.textContent = `
    @keyframes bukietowa-marquee {
      0%   { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    .bukietowa-ticker-track {
      display: inline-flex;
      white-space: nowrap;
      will-change: transform;
    }
  `;
  document.head.appendChild(style);
}

function NameDaysTicker({ names, label }: { names: string[]; label: string }) {
  const tickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { ensureTickerStyles(); }, []);

  // Join all names with bullet separators; duplicate for seamless loop
  const text = names.join('  ·  ');
  const fullText = text + '     ' + text; // duplicate for seamless loop

  // Speed: ~80px/s — longer list scrolls at same pace
  const approxPxPerChar = 8.5;
  const duration = Math.max(12, (text.length * approxPxPerChar) / 80);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'clamp(6px, 1vw, 10px)',
      flexWrap: 'nowrap' as const,
      flexShrink: 1,
      minWidth: 0,
      minHeight: 28,
      overflow: 'hidden',
    }}>
      <GiftIcon />
      <span style={{
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 'clamp(12px, 1.1vw, 14px)',
        color: DARK,
        whiteSpace: 'nowrap' as const,
        fontWeight: 400,
        flexShrink: 0,
      }}>
        {label}
      </span>
      <div
        ref={tickerRef}
        style={{
          overflow: 'hidden',
          flexShrink: 1,
          minWidth: 0,
          maxWidth: 420,
        }}
      >
        <span
          className="bukietowa-ticker-track"
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 'clamp(12px, 1.1vw, 14px)',
            color: ROSE,
            fontWeight: 500,
            animation: `bukietowa-marquee ${duration}s linear infinite`,
          }}
        >
          {fullText}
        </span>
      </div>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────── */
export function TodayDeliveryBanner({ config }: { config: TodayDeliveryBannerConfig }) {
  const {
    countdownLabel, nextDeliveryPrefix, nextDeliveryFromWord,
    namedaysTodayLabel, namedaysTomorrowLabel,
    hourUnit, minuteUnit, secondUnit,
  } = config;
  const { bannerState, isCountdown, hh, mm, ss } = useDeliveryCountdown();
  const [isTomorrow, setIsTomorrow] = useState<boolean>(() => getNamedayDate().isTomorrow);
  const [namedays, setNamedays] = useState<string[]>(() => {
    const { month, day } = getNamedayDate();
    return sortByPopularity(getNameDayNames(month, day));
  });

  // The local calendar is immediately usable; refreshing it is background work.
  useEffect(() => {
    if (isPrerenderMode) return;
    let active = true;
    const { month, day, isTomorrow: tomorrow } = getNamedayDate();
    setIsTomorrow(tomorrow);
    const cancel = scheduleNonCriticalTask(() => {
      void fetchNameDays(month, day).then((names) => {
        if (active && names.length > 0) setNamedays(sortByPopularity(names));
      });
    });
    return () => { active = false; cancel(); };
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        zIndex: 10,
        width: '100%',
        background: '#fff',
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          height: 54,
          padding: '13px clamp(20px, 4vw, 48px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'clamp(36px, 6vw, 96px)',
          flexWrap: 'nowrap' as const,
          overflow: 'hidden',
        }}
      >
        {/* ── LEFT: Countdown ─────────────────────────────── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(6px, 1.2vw, 14px)',
          minHeight: 28,
          flexWrap: 'nowrap' as const,
        }}>
          <ClockIcon />

          {/* Label */}
          <span style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 'clamp(12px, 1.1vw, 14px)',
            color: DARK,
            whiteSpace: 'nowrap' as const,
            fontWeight: 400,
          }}>
            {isCountdown
              ? countdownLabel
              : (
                <>
                  {nextDeliveryPrefix}{' '}
                  <span style={{ fontWeight: 600, color: GOLD2 }}>
                    {(bannerState as Extract<BannerState, { kind: 'next' }>).dateLabel},{' '}
                    {(bannerState as Extract<BannerState, { kind: 'next' }>).dateDDMM}
                  </span>
                  {' '}{nextDeliveryFromWord}{' '}
                  <span style={{ fontWeight: 600, color: GOLD2 }}>
                    {(bannerState as Extract<BannerState, { kind: 'next' }>).startHour}
                  </span>
                </>
              )
            }
          </span>

          {/* Time blocks */}
          {isCountdown && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(4px, 0.8vw, 10px)', flexWrap: 'nowrap' as const }}>
              <TimeBlock value={hh} unit={hourUnit} />
              <span style={{ color: GOLD, fontSize: 16, fontWeight: 300, lineHeight: 1, margin: '0 -2px' }}>:</span>
              <TimeBlock value={mm} unit={minuteUnit} />
              <span style={{ color: GOLD, fontSize: 16, fontWeight: 300, lineHeight: 1, margin: '0 -2px' }}>:</span>
              <TimeBlock value={ss} unit={secondUnit} />
            </div>
          )}
        </div>

        {/* ── RIGHT: Name days ticker ──────────────────────── */}
        {namedays.length > 0 && (
          <NameDaysTicker names={namedays} label={isTomorrow ? namedaysTomorrowLabel : namedaysTodayLabel} />
        )}
      </div>
    </div>
  );
}
